import { useState } from 'react';

import { cn } from '@/shared/lib/cn';
import { getAvatarColorFromName } from '@/shared/utils/avatar-color';
import { getInitials } from '@/shared/utils/format';

export interface AvatarProps {
  src?: string | null;
  name: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const sizeStyles = {
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-12 w-12 text-base',
};

const avatarBaseClassName =
  'shrink-0 rounded-full object-cover shadow-sm ring-1 ring-content-border';

function hasValidSrc(src?: string | null): src is string {
  return typeof src === 'string' && src.trim().length > 0;
}

export function Avatar({ src, name, size = 'md', className }: AvatarProps) {
  const [imageError, setImageError] = useState(false);
  const showImage = hasValidSrc(src) && !imageError;

  if (showImage) {
    return (
      <img
        src={src}
        alt={name}
        onError={() => {
          setImageError(true);
        }}
        className={cn(avatarBaseClassName, sizeStyles[size], className)}
      />
    );
  }

  const avatarColor = getAvatarColorFromName(name);

  return (
    <div
      aria-label={name}
      style={avatarColor}
      className={cn(
        'flex items-center justify-center font-semibold uppercase tracking-wide',
        avatarBaseClassName,
        sizeStyles[size],
        className,
      )}
    >
      {getInitials(name)}
    </div>
  );
}
